// base
import React, { useCallback, useEffect, useState, useRef, useMemo } from 'react';

// styles
import { StyledLazyImage } from './style';

// modules
import { userApi } from 'modules/user/apis/user.api';
import { workerApi } from 'modules/worker/apis/worker.api';

const placeholder =
  'data:image/gif;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAFklEQVR42mN8//HLfwYiAOOoQpoqBABbWyZJf74GZgAAAABJRU5ErkJggg==';

interface LazyImageProps {
  src: string;
  alt?: string;
  width?: string | number;
  height?: string | number;
  isCircle?: boolean;
  isAttendance?: boolean;
  isUserFileId?: boolean;
  thumbId?: string;
  [key: string]: any;
}

export const LazyImage: React.FC<LazyImageProps> = ({
  src,
  alt = '',
  width,
  height,
  isCircle = false,
  isAttendance = false,
  isUserFileId,
  thumbId,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInView, setIsInView] = useState(false);
  const [imageSrc, setImageSrc] = useState<string | null>(null);
  const imageRef = useRef<HTMLImageElement>(null);

  const getDisplaySrc = useMemo(() => {
    if (!isInView) return placeholder;
    if (isUserFileId) return imageSrc || placeholder;

    return imageSrc || src || placeholder;
  }, [imageSrc, isInView, isUserFileId, src]);

  const getThumb = useCallback(async () => {
    if (!isUserFileId) {
      setImageSrc(src);
      return;
    }

    try {
      if (!thumbId) {
        setImageSrc(null);
        return;
      }

      if (isAttendance) {
        const res = await workerApi.getWorkerDownloadAPI(thumbId);
        setImageSrc(res);
        return;
      }

      const res = await userApi.getUserDownloadAPI(thumbId);
      setImageSrc(res);
    } catch (e) {
      console.error(e);
      setImageSrc(null);
    }
  }, [isAttendance, isUserFileId, src, thumbId]);

  const handleImageLoad = () => {
    setIsLoaded(true);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.1,
        rootMargin: '50px'
      }
    );

    if (imageRef.current) {
      observer.observe(imageRef.current);
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    getThumb();
  }, [getThumb]);

  return (
    <StyledLazyImage
      ref={imageRef}
      src={getDisplaySrc}
      alt={alt}
      width={width}
      height={height}
      $isCircle={isCircle}
      $isLoaded={isLoaded}
      onLoad={handleImageLoad}
      className={`image${isLoaded ? ' loaded' : ' loading'}`}
      {...props}
    />
  );
};
